import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchSemester,
  fetchClassTemplates,
  addSemesterClass,
  updateSemesterClass,
  removeSemesterClass,
  publishSemester,
  revertSemester,
  fetchUsersByIds,
} from "../api/backend";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import ScheduleClassDialog from "../components/ScheduleClassDialog";
import TemplateRail from "../components/semesterPlanner/TemplateRail";
import MonthGrid from "../components/semesterPlanner/MonthGrid";
import TermTimeline from "../components/semesterPlanner/TermTimeline";
import ClassInspector from "../components/semesterPlanner/ClassInspector";
import ReadinessPanel from "../components/semesterPlanner/ReadinessPanel";
import { CATEGORY_COLORS, colorFor, userName } from "../components/semesterPlanner/plannerConstants";
import {
  addWeekdays,
  classDates,
  classSpan,
  formatDay,
  formatISO,
  parseDate,
  readiness,
} from "../components/semesterPlanner/plannerUtils";
import "../components/semesterPlanner/semesterPlanner.css";

function StatusBadge({ status }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  const tAccent = theme.palette.warning.main;
  const configs = {
    DRAFT: { label: "Draft", bg: alpha(tAccent, 0.10), color: tAccent },
    PUBLISHED: { label: "Published", bg: alpha(tSecondary, 0.10), color: tSecondary },
    ARCHIVED: { label: "Archived", bg: "var(--color-surface-press-light)", color: "var(--color-ink-3)" },
  };
  const cfg = configs[status] || configs.ARCHIVED;
  return (
    <Box
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        px: "10px",
        py: "3px",
        borderRadius: "999px",
        background: cfg.bg,
        color: cfg.color,
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        flexShrink: 0,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.color,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </Box>
  );
}

export default function SemesterPlanningPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const tSecondaryDk = theme.palette.secondary.dark;
  const tAccent = theme.palette.warning.main;
  const { id } = useParams();
  const navigate = useNavigate();

  const [semester, setSemester] = useState(null);
  const [semesterClasses, setSemesterClasses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Schedule dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleTemplate, setScheduleTemplate] = useState(null);
  const [scheduleStartDate, setScheduleStartDate] = useState("");
  const [qualifiedInstructors, setQualifiedInstructors] = useState([]);

  // Publish dialog
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishDate, setPublishDate] = useState("");
  const [publishing, setPublishing] = useState(false);

  // Revert to draft dialog
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Edit session dialog
  const [editSessionOpen, setEditSessionOpen] = useState(false);
  const [editSessionClass, setEditSessionClass] = useState(null);
  const [newSession, setNewSession] = useState({
    date: "",
    startTime: "08:00",
    endTime: "16:00",
  });

  const [draggedTemplate, setDraggedTemplate] = useState(null);

  const loadSeq = useRef(0);

  // Reloads after edits are silent so the board keeps its state; only the
  // initial load for an id shows the spinner.
  const loadData = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const semData = await fetchSemester(id);
      if (seq !== loadSeq.current) return;
      setSemester(semData);
      setSemesterClasses(semData.classes || []);
      const tplData = await fetchClassTemplates(semData.tenantId).catch(() => []);
      if (seq !== loadSeq.current) return;
      setTemplates(Array.isArray(tplData) ? tplData : []);
    } catch {
      if (seq === loadSeq.current) setError("Failed to load semester data.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  // ── Drag & drop ──────────────────────────────────────────────────────────────

  const handleTemplateCardDragStart = (e, tpl) => {
    setDraggedTemplate(tpl);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        templateId: tpl.id,
        title: tpl.title,
        category: tpl.category,
        totalHours: tpl.totalHours,
        maxStudents: tpl.capacity || tpl.availableSeats || 24,
        qualifiedInstructorIds: tpl.qualifiedInstructorIds,
      }),
    );
  };

  // ── Scheduled hours helper (used by sidebar progress bars) ─────────────────

  const getScheduledHours = (sc) => {
    return (Array.isArray(sc.scheduledDates) ? sc.scheduledDates : []).reduce(
      (sum, d) => {
        if (!d.startTime || !d.endTime) return sum;
        const [sh, sm] = d.startTime.split(":").map(Number);
        const [eh, em] = d.endTime.split(":").map(Number);
        return sum + (eh + em / 60 - (sh + sm / 60));
      },
      0,
    );
  };

  // Flatten all existing scheduled events for conflict detection
  const existingEvents = semesterClasses.flatMap((sc) =>
    (Array.isArray(sc.scheduledDates) ? sc.scheduledDates : []).map((d) => ({
      date: d.date,
      startTime: d.startTime,
      room: sc.location,
      instructor: sc.instructorId,
    }))
  );

  // ── Schedule dialog handlers ─────────────────────────────────────────────────

  const openScheduleDialog = async (tpl, dateStr) => {
    setScheduleTemplate({
      id: tpl.id || tpl.templateId,
      title: tpl.title,
      category: tpl.category,
      totalHours: tpl.totalHours || 0,
      sessionCount: tpl.sessionCount || 0,
      maxStudents: tpl.capacity || tpl.availableSeats || tpl.maxStudents || 24,
      code: tpl.courseNumber || tpl.code || "",
    });
    setScheduleStartDate(dateStr || "");
    if (tpl.qualifiedInstructorIds?.length > 0) {
      try {
        const users = await fetchUsersByIds(tpl.qualifiedInstructorIds);
        setQualifiedInstructors(Array.isArray(users) ? users : []);
      } catch {
        setQualifiedInstructors([]);
      }
    } else {
      setQualifiedInstructors([]);
    }
    setScheduleDialogOpen(true);
  };

  const handleScheduleConfirm = async (payload) => {
    const { template, location, maxSeats: capacity, instructorId, sessions } = payload;
    const newDates = sessions.map(({ date, startTime, endTime }) => ({
      date,
      startTime,
      endTime,
    }));
    try {
      const existing = semesterClasses.find(
        (sc) => String(sc.submissionId) === String(template.id),
      );
      if (existing) {
        const existingDates = Array.isArray(existing.scheduledDates)
          ? existing.scheduledDates
          : [];
        await updateSemesterClass(id, existing.id, {
          scheduledDates: [...existingDates, ...newDates],
          ...(instructorId && { instructorId }),
          ...(location && { location }),
          ...(capacity && { capacity }),
        });
      } else {
        await addSemesterClass(id, {
          submissionId: template.id,
          title: template.title,
          category: template.category,
          instructorId: instructorId || null,
          location,
          capacity,
          totalHours: template.totalHours,
          scheduledDates: newDates,
        });
      }
      setScheduleDialogOpen(false);
      setScheduleTemplate(null);
      setSuccess(
        `${sessions.length} session${sessions.length > 1 ? "s" : ""} scheduled successfully.`
      );
      loadData();
    } catch {
      setError("Failed to schedule class.");
    }
  };

  // ── Save / remove ────────────────────────────────────────────────────────────

  const handleAddSession = async () => {
    if (!editSessionClass || !newSession.date) return;
    const existingDates = Array.isArray(editSessionClass.scheduledDates)
      ? editSessionClass.scheduledDates
      : [];
    try {
      await updateSemesterClass(id, editSessionClass.id, {
        scheduledDates: [
          ...existingDates,
          {
            date: newSession.date,
            startTime: newSession.startTime,
            endTime: newSession.endTime,
          },
        ],
      });
      setEditSessionOpen(false);
      setSuccess("Session added successfully.");
      loadData();
    } catch {
      setError("Failed to add session.");
    }
  };

  const handleRemoveClass = async (scId) => {
    try {
      await removeSemesterClass(id, scId);
      setEditSessionOpen(false);
      loadData();
      return true;
    } catch {
      setError("Failed to remove class.");
      return false;
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishSemester(id);
      setPublishDialogOpen(false);
      setSuccess("Semester published! Classes are now in the catalog.");
      loadData();
    } catch {
      setError("Failed to publish semester.");
    } finally {
      setPublishing(false);
    }
  };

  const handleRevert = async () => {
    setReverting(true);
    try {
      await revertSemester(id);
      setRevertDialogOpen(false);
      setSuccess("Semester moved back to draft. You can now edit it.");
      loadData();
    } catch (err) {
            const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Failed to revert semester.";
      setError(typeof msg === "string" ? msg : "Failed to revert semester.");
    } finally {
      setReverting(false);
    }
  };

  // ── Planner view state ───────────────────────────────────────────────────────

  const [view, setView] = useState("month");
  const [monthIdx, setMonthIdx] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [placingId, setPlacingId] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [instructorNames, setInstructorNames] = useState({});

  const today = formatISO(new Date());
  const report = useMemo(() => readiness(semesterClasses), [semesterClasses]);
  const conflictIds = useMemo(
    () => new Set(report.conflicts.flatMap((c) => [c.a, c.b])),
    [report],
  );

  const months = useMemo(() => {
    if (!semester?.startDate || !semester?.endDate) return [];
    const out = [];
    const d = parseDate(semester.startDate);
    d.setDate(1);
    while (formatISO(d) <= semester.endDate) {
      out.push({ y: d.getFullYear(), m: d.getMonth() });
      d.setMonth(d.getMonth() + 1);
    }
    return out;
  }, [semester?.startDate, semester?.endDate]);

  useEffect(() => {
    if (!months.length) return;
    const t = parseDate(formatISO(new Date()));
    const i = months.findIndex((m) => m.m === t.getMonth() && m.y === t.getFullYear());
    setMonthIdx(i >= 0 ? i : 0);
  }, [months]);

  useEffect(() => {
    const ids = [...new Set(semesterClasses.map((c) => c.instructorId).filter(Boolean))];
    const missing = ids.filter((i) => !(i in instructorNames));
    if (!missing.length) return undefined;
    let cancelled = false;
    fetchUsersByIds(missing)
      .then((users) => {
        if (cancelled) return;
        const found = Object.fromEntries(
          (Array.isArray(users) ? users : []).map((u) => [u.id, userName(u)]),
        );
        setInstructorNames((prev) => ({
          ...prev,
          ...Object.fromEntries(missing.map((i) => [i, found[i] || `User ${i}`])),
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [semesterClasses, instructorNames]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        const t = e.target;
        const typing = t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName);
        if (e.defaultPrevented || typing || document.querySelector('[role="dialog"]')) return;
        setPlacingId(null);
        setHoverDate(null);
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const placingTpl = templates.find((t) => t.id === placingId) || null;
  const activeTpl = draggedTemplate || placingTpl;
  const classDays = (tpl) =>
    Math.max(1, tpl.sessionCount || Math.ceil((tpl.totalHours || 8) / 8));
  const previewEnd = activeTpl && hoverDate ? addWeekdays(hoverDate, classDays(activeTpl)) : null;
  const previewRange = previewEnd
    ? { start: hoverDate, end: previewEnd, ok: previewEnd <= semester?.endDate }
    : null;

  const endDrag = () => {
    setDraggedTemplate(null);
    setHoverDate(null);
  };

  const editable = semester?.status?.toUpperCase() === "DRAFT";

  useEffect(() => {
    if (!editable) {
      setPlacingId(null);
      setHoverDate(null);
    }
  }, [editable]);

  const handleDropAt = (dateStr) => {
    const tpl = activeTpl;
    setDraggedTemplate(null);
    setHoverDate(null);
    setPlacingId(null);
    if (tpl && editable) openScheduleDialog(tpl, dateStr);
  };

  const pickClass = (cid) => {
    if (cid == null) {
      setSelectedId(null);
      return;
    }
    setSelectedId(cid);
    setReadinessOpen(false);
    const sc = semesterClasses.find((c) => c.id === cid);
    const span = sc && classSpan(sc);
    if (span && view === "month") {
      const s = parseDate(span.start);
      const i = months.findIndex((m) => m.m === s.getMonth() && m.y === s.getFullYear());
      if (i >= 0) setMonthIdx(i);
    }
  };

  const monthCount = (m) =>
    semesterClasses.filter((c) =>
      classDates(c).some((d) => {
        const p = parseDate(d);
        return p.getMonth() === m.m && p.getFullYear() === m.y;
      }),
    ).length;

  const selectedClass = semesterClasses.find((c) => c.id === selectedId) || null;
  const sideOpen = !!selectedClass || readinessOpen;
  const usedCategories = [...new Set(semesterClasses.map((c) => c.category || "Other"))];

  // ── Early returns ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!semester) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Semester not found.</Alert>
      </Box>
    );
  }

  const isDraft = semester.status?.toUpperCase() === "DRAFT";

  const underScheduledClasses = semesterClasses.filter(
    (sc) => sc.totalHours > 0 && getScheduledHours(sc) < sc.totalHours,
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box className={`sp-root${draggedTemplate ? " sp-dragging" : ""}`} sx={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--color-surface-2)" }}>

      {/* ── Sub-header bar ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: "var(--color-surface-2)",
          borderBottom: "1px solid var(--color-hairline-cloud)",
          px: 3.5,
          py: "14px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <IconButton
          size="small"
          aria-label="Back to semesters"
          onClick={() => navigate("/semesters")}
          sx={{
            border: "1.5px solid var(--color-hairline-cloud)",
            borderRadius: "9px",
            color: "var(--color-ink-3)",
            "&:hover": { background: "var(--color-surface-press-light)", color: "var(--color-ink)" },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <Typography
            sx={{
              fontSize: "21px",
              fontWeight: 800,
              letterSpacing: "-.025em",
              color: "var(--color-ink)",
              fontFamily: "var(--font-display)",
            }}
          >
            {semester.name}
          </Typography>
          <StatusBadge status={semester.status} />
        </Box>

        {(semester.startDate || semester.endDate) && (
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "13px",
              color: "var(--color-ink-3)",
              display: "flex",
              alignItems: "center",
              gap: "7px",
            }}
          >
            {[semester.startDate, semester.endDate].filter(Boolean).join(" — ")}
          </Typography>
        )}

        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: "9px",
            flexWrap: "wrap",
          }}
        >
          {isDraft && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                fontSize: "12.5px",
                fontWeight: 600,
                color: tAccent,
                background: alpha(tAccent, 0.10),
                border: `1px solid ${alpha(tAccent, 0.22)}`,
                px: "12px",
                py: "7px",
                borderRadius: "10px",
              }}
            >
              <EditIcon sx={{ fontSize: 13 }} />
              Editing draft
            </Box>
          )}
          {isDraft && (
            <button
              type="button"
              className={`sp-go rdb${report.issueCount ? " iss" : ""}${readinessOpen ? " on" : ""}`}
              data-testid="planner-readiness-btn"
              onClick={() => {
                setReadinessOpen(!readinessOpen);
                setSelectedId(null);
              }}
            >
              {report.issueCount ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : <CheckCircleIcon sx={{ fontSize: 14 }} />}
              {report.issueCount
                ? `${report.issueCount} ${report.issueCount === 1 ? "issue" : "issues"}`
                : "Ready"}
            </button>
          )}
          <Button
            size="small"
            startIcon={<SettingsIcon sx={{ fontSize: "13px !important" }} />}
            sx={{
              height: 34,
              px: "12px",
              borderRadius: "9px",
              border: "1.5px solid var(--color-hairline-cloud)",
              color: "var(--color-ink-2)",
              background: "var(--color-surface-2)",
              fontSize: "12.5px",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { background: "var(--color-surface-press-light)", borderColor: "var(--color-hairline-cool)" },
            }}
          >
            Semester Settings
          </Button>
          {isDraft && semesterClasses.length > 0 && (
            <Tooltip title={report.blocking.length ? "Resolve instructor double-bookings before publishing" : ""}>
            <span>
            <Button
              size="small"
              disabled={report.blocking.length > 0}
              startIcon={<PublishIcon sx={{ fontSize: "13px !important" }} />}
              onClick={() => {
                setPublishDate(formatISO(new Date()));
                setPublishDialogOpen(true);
              }}
              sx={{
                height: 34,
                px: "12px",
                borderRadius: "9px",
                background: tPrimary,
                color: "#fff",
                fontSize: "12.5px",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: tPrimaryDk, boxShadow: "none" },
                "&.Mui-disabled": {
                  background: "var(--color-surface-press-light)",
                  color: "var(--color-ink-3)",
                },
              }}
            >
              Publish Semester
            </Button>
            </span>
            </Tooltip>
          )}
          {(semester.status || "").toUpperCase() === "PUBLISHED" && (
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: "13px !important" }} />}
              onClick={() => setRevertDialogOpen(true)}
              sx={{
                height: 34,
                px: "12px",
                borderRadius: "9px",
                border: `1.5px solid ${alpha(tAccent, 0.35)}`,
                background: alpha(tAccent, 0.06),
                color: tAccent,
                fontSize: "12.5px",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: alpha(tAccent, 0.12), borderColor: tAccent },
              }}
            >
              Edit Semester
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mx: 2, mt: 1, flexShrink: 0 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* ── Planner board ──────────────────────────────────────────────────── */}
      <div className={`sp-bd${sideOpen ? " w-side" : ""}`}>
        <TemplateRail
          templates={templates}
          classes={semesterClasses}
          placingId={placingId}
          canEdit={isDraft}
          open={tplOpen}
          onClose={() => setTplOpen(false)}
          onSelectPlacing={(tid) => {
            setPlacingId(tid);
            setHoverDate(null);
            setTplOpen(false);
          }}
          onDragStart={(e, tpl) => {
            handleTemplateCardDragStart(e, tpl);
            // Closing synchronously cancels the drag in Chrome on narrow layouts.
            setTimeout(() => setTplOpen(false), 0);
          }}
          onDragEnd={endDrag}
        />
        {tplOpen && <div className="sp-scrim" onClick={() => setTplOpen(false)} />}
        <main className="sp-cal">
          <div className="sp-tb">
            <button type="button" className="sp-go sp-tpb" onClick={() => setTplOpen(true)}>
              Templates
            </button>
            {view === "month" ? (
              <div className="sp-mt">
                <button type="button" className="sp-pib" disabled={monthIdx === 0} onClick={() => setMonthIdx(monthIdx - 1)} aria-label="Previous month">
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </button>
                {months.map((m, i) => (
                  <button key={`${m.y}-${m.m}`} type="button" className={`sp-mtb${i === monthIdx ? " on" : ""}`} onClick={() => setMonthIdx(i)}>
                    {formatDay(formatISO(new Date(m.y, m.m, 1)), { month: "short" })}
                    <em>{monthCount(m)}</em>
                  </button>
                ))}
                <button type="button" className="sp-pib" disabled={monthIdx === months.length - 1} onClick={() => setMonthIdx(monthIdx + 1)} aria-label="Next month">
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            ) : (
              <h2 className="sp-tb-h">Semester overview</h2>
            )}
            <div className="sp-seg">
              <button type="button" className={view === "month" ? "on" : ""} onClick={() => setView("month")}>Month</button>
              <button type="button" className={view === "term" ? "on" : ""} onClick={() => setView("term")}>Semester</button>
            </div>
          </div>
          <div className="sp-lg">
            {usedCategories.map((c) => (
              <span key={c}><i style={{ background: colorFor(c) }} />{c}</span>
            ))}
            <span className="sp-lg-k"><b className="sp-k-ua" />No instructor</span>
            <span className="sp-lg-k"><WarningAmberIcon sx={{ fontSize: 12 }} />Conflict</span>
          </div>
          {placingTpl && (
            <div className="sp-plc" role="status">
              <span>Placing <b>{placingTpl.title}</b>. Click a start day.</span>
              <button type="button" className="sp-go" onClick={() => { setPlacingId(null); setHoverDate(null); }}>Cancel</button>
            </div>
          )}
          <div className="sp-cal-b" onMouseLeave={() => placingTpl && setHoverDate(null)}>
            {view === "month" && months[monthIdx] ? (
              <MonthGrid
                month={months[monthIdx]}
                classes={semesterClasses}
                conflictIds={conflictIds}
                semester={semester}
                today={today}
                selectedId={selectedId}
                placing={!!placingTpl}
                hoverDate={hoverDate}
                previewRange={previewRange}
                canEdit={isDraft}
                instructorNames={instructorNames}
                onSelect={pickClass}
                onHover={setHoverDate}
                onDropAt={handleDropAt}
              />
            ) : !months.length ? (
              <p className="sp-emp">Set a start and end date for this semester to plan classes.</p>
            ) : (
              <TermTimeline
                classes={semesterClasses}
                conflictIds={conflictIds}
                semester={semester}
                today={today}
                selectedId={selectedId}
                instructorNames={instructorNames}
                onSelect={pickClass}
              />
            )}
          </div>
        </main>
        {sideOpen && (
          <>
            <div className="sp-scrim sd" onClick={() => { setSelectedId(null); setReadinessOpen(false); }} />
            <aside className="sp-ins">
              {selectedClass ? (
                <ClassInspector
                  key={selectedClass.id}
                  sc={selectedClass}
                  classes={semesterClasses}
                  conflicts={report.conflicts}
                  instructorName={instructorNames[selectedClass.instructorId]}
                  canEdit={isDraft}
                  onClose={() => setSelectedId(null)}
                  onEditSessions={() => {
                    setEditSessionClass(selectedClass);
                    setNewSession({ date: "", startTime: "08:00", endTime: "16:00" });
                    setEditSessionOpen(true);
                  }}
                  onRemove={async () => {
                    if (await handleRemoveClass(selectedClass.id)) setSelectedId(null);
                  }}
                />
              ) : (
                <ReadinessPanel
                  report={report}
                  classes={semesterClasses}
                  instructorNames={instructorNames}
                  onSelect={pickClass}
                  onClose={() => setReadinessOpen(false)}
                />
              )}
            </aside>
          </>
        )}
      </div>

      {/* ── Schedule Class Dialog ──────────────────────────────────────────── */}
      <ScheduleClassDialog
        open={scheduleDialogOpen}
        template={scheduleTemplate}
        startDate={scheduleStartDate}
        existingEvents={existingEvents}
        instructors={qualifiedInstructors}
        onConfirm={handleScheduleConfirm}
        onClose={() => { setScheduleDialogOpen(false); setScheduleTemplate(null); }}
      />


      {/* ── Event Detail / Edit Session Dialog ────────────────────────────── */}
      <Dialog
        open={editSessionOpen}
        onClose={() => setEditSessionOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        {editSessionClass && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "13px",
                p: "22px 24px 18px",
                borderBottom: "1px solid var(--color-surface-press-light)",
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  background:
                    CATEGORY_COLORS[editSessionClass.category] ||
                    CATEGORY_COLORS.Other,
                  color: "#fff",
                }}
              >
                <SchoolIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>
                  {editSessionClass.title}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "var(--color-ink-3)", mt: "3px" }}>
                  {editSessionClass.location || editSessionClass.category || "Class details"}
                </Typography>
              </Box>
              {isDraft && (
                <Tooltip title="Remove from semester">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveClass(editSessionClass.id)}
                    sx={{
                      border: "1.5px solid rgba(180,35,24,.18)",
                      borderRadius: "9px",
                      color: "#B42318",
                      background: "rgba(180,35,24,.08)",
                      "&:hover": { background: "rgba(180,35,24,.18)" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <DialogContent sx={{ p: "20px 24px" }}>
              {/* Hours progress */}
              {(() => {
                const scheduled = getScheduledHours(editSessionClass);
                const total = editSessionClass.totalHours || 0;
                const pct = total > 0 ? Math.min(100, (scheduled / total) * 100) : 0;
                return (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: "6px" }}>
                      <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--color-ink-3)" }}>
                        Hours Scheduled
                      </Typography>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--color-ink)" }}>
                        {scheduled.toFixed(0)} / {total} hrs
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      color={pct >= 100 ? "success" : "warning"}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    {pct < 100 && total > 0 && (
                      <Typography sx={{ fontSize: "11.5px", color: tAccent, mt: "4px" }}>
                        {(total - scheduled).toFixed(0)} hours remaining
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              {/* Session list */}
              <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--color-ink-3)", mb: "10px", display: "flex", justifyContent: "space-between" }}>
                <span>Session Dates</span>
                <span>{(editSessionClass.scheduledDates || []).length} scheduled</span>
              </Typography>

              <Stack spacing={1} sx={{ mb: 2 }}>
                {(editSessionClass.scheduledDates || []).map((d, idx) => {
                  const dateObj = d.date ? new Date(d.date + "T12:00:00") : null;
                  const mon = dateObj
                    ? dateObj.toLocaleString("en-US", { month: "short" }).toUpperCase()
                    : "";
                  const day = dateObj ? dateObj.getDate() : "";
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "11px",
                        p: "11px 13px",
                        border: "1.5px solid var(--color-surface-press-light)",
                        borderRadius: "11px",
                        background: "var(--color-surface-2)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: 40,
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: ".06em", color: "var(--color-ink-3)", textTransform: "uppercase" }}>
                          {mon}
                        </Typography>
                        <Typography sx={{ fontSize: "19px", fontWeight: 800, color: tPrimary, lineHeight: 1 }}>
                          {day}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--color-ink)" }}>
                          Session {idx + 1}
                        </Typography>
                        <Typography sx={{ fontSize: "11.5px", color: "var(--color-ink-3)", mt: "1px" }}>
                          {d.startTime} – {d.endTime}
                          {d.location ? ` · ${d.location}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>

              {/* Add new session */}
              {isDraft && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--color-ink-3)", mb: "10px" }}>
                    Add Session
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Date"
                      type="date"
                      fullWidth
                      value={newSession.date}
                      onChange={(e) =>
                        setNewSession((s) => ({ ...s, date: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "11px" } }}
                    />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <TextField
                        label="Start Time"
                        type="time"
                        fullWidth
                        value={newSession.startTime}
                        onChange={(e) =>
                          setNewSession((s) => ({ ...s, startTime: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "11px" } }}
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        fullWidth
                        value={newSession.endTime}
                        onChange={(e) =>
                          setNewSession((s) => ({ ...s, endTime: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "11px" } }}
                      />
                    </Box>
                  </Stack>
                </>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                px: "24px",
                py: "16px",
                background: "var(--color-surface-2)",
                borderTop: "1px solid var(--color-surface-press-light)",
                gap: "9px",
              }}
            >
              <Button
                onClick={() => setEditSessionOpen(false)}
                sx={{
                  height: 34,
                  px: "13px",
                  borderRadius: "9px",
                  border: "1.5px solid var(--color-hairline-cloud)",
                  color: "var(--color-ink-3)",
                  fontSize: "13px",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { background: "var(--color-surface-press-light)" },
                }}
              >
                Close
              </Button>
              {isDraft && (
                <Button
                  onClick={handleAddSession}
                  disabled={!newSession.date}
                  sx={{
                    height: 34,
                    px: "13px",
                    borderRadius: "9px",
                    background: tPrimary,
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { background: tPrimaryDk, boxShadow: "none" },
                    "&.Mui-disabled": { opacity: 0.5, color: "#fff", background: tPrimary },
                  }}
                >
                  Add Session
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Publish Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "13px",
            p: "22px 24px 18px",
            borderBottom: "1px solid var(--color-surface-press-light)",
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              background: tSecondary,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <PublishIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>
              Publish {semester.name}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "var(--color-ink-3)", mt: "3px" }}>
              Lock the semester and make its schedule visible to students.
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: "20px 24px" }}>
          <Stack spacing={2}>
            {/* Warning */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "11px",
                background: alpha(tAccent, 0.10),
                border: `1.5px solid ${alpha(tAccent, 0.22)}`,
                borderRadius: "12px",
                p: "13px 15px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 16, color: tAccent, mt: "1px", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "13px", color: "var(--color-ink-2)", lineHeight: 1.5 }}>
                Once published, the semester becomes read-only. You'll need to revert to draft to make further edits.
              </Typography>
            </Box>

            {/* Readiness warnings (non-blocking) */}
            {(report.unassigned.length > 0 || report.warnings.length > 0) && (
              <Alert severity="warning" data-testid="publish-readiness-warning">
                {report.unassigned.length > 0 &&
                  `${report.unassigned.length} ${report.unassigned.length === 1 ? "class has" : "classes have"} no instructor assigned. `}
                {report.warnings.length > 0 &&
                  `${report.warnings.length} possible room ${report.warnings.length === 1 ? "conflict" : "conflicts"}.`}
              </Alert>
            )}

            {/* Under-scheduled warnings */}
            {underScheduledClasses.length > 0 && (
              <Alert severity="warning">
                Some classes have fewer scheduled hours than required:
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
                  {underScheduledClasses.map((sc) => (
                    <li key={sc.id}>
                      {sc.title}: {getScheduledHours(sc).toFixed(0)} / {sc.totalHours} hrs
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Readiness check */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "11px",
                p: "13px 15px",
                background: "var(--color-surface-2)",
                border: "1.5px solid var(--color-surface-press-light)",
                borderRadius: "12px",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 18, color: tSecondary, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "13px", color: "var(--color-ink-2)" }}>
                <Box component="strong" sx={{ color: "var(--color-ink)" }}>
                  {semesterClasses.length} classes
                </Box>
                {" ready"}
                {underScheduledClasses.length === 0 && report.issueCount === 0 && " · no unresolved conflicts"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--color-ink-3)" }}>
                Publish Date{" "}
                <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "var(--color-ink-4)", fontSize: "11px" }}>
                  — when students can see it
                </Box>
              </Typography>
              <TextField
                type="date"
                fullWidth
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "11px", background: "var(--color-surface-2)" } }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: "24px",
            py: "16px",
            background: "var(--color-surface-2)",
            borderTop: "1px solid var(--color-surface-press-light)",
            gap: "9px",
          }}
        >
          <Button
            onClick={() => setPublishDialogOpen(false)}
            sx={{
              height: 34,
              px: "13px",
              borderRadius: "9px",
              border: "1.5px solid var(--color-hairline-cloud)",
              color: "var(--color-ink-3)",
              fontSize: "13px",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { background: "var(--color-surface-press-light)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={publishing}
            sx={{
              height: 34,
              px: "13px",
              borderRadius: "9px",
              background: tSecondary,
              color: "#fff",
              fontSize: "13px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: tSecondaryDk, boxShadow: "none" },
              "&.Mui-disabled": { opacity: 0.5, color: "#fff", background: tSecondary },
            }}
          >
            {publishing ? "Publishing..." : "Publish Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Revert to Draft Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={revertDialogOpen}
        onClose={() => setRevertDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        <Box sx={{ p: "22px 24px 18px", borderBottom: "1px solid var(--color-surface-press-light)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "11px",
                background: alpha(tAccent, 0.10),
                color: tAccent,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "17px", fontWeight: 800, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>
                Reopen for Editing?
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "var(--color-ink-3)", mt: "2px" }}>
                This will move the semester back to draft.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ p: "18px 24px" }}>
          <Alert
            severity="warning"
            icon={false}
            sx={{ borderRadius: "11px", fontSize: "13px" }}
          >
            Classes already created in the catalog from the last publish will
            not be removed. Only the semester status changes.
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: "24px",
            py: "16px",
            background: "var(--color-surface-2)",
            borderTop: "1px solid var(--color-surface-press-light)",
            gap: "9px",
          }}
        >
          <Button
            onClick={() => setRevertDialogOpen(false)}
            sx={{
              height: 34,
              px: "13px",
              borderRadius: "9px",
              border: "1.5px solid var(--color-hairline-cloud)",
              color: "var(--color-ink-3)",
              fontSize: "13px",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { background: "var(--color-surface-press-light)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevert}
            disabled={reverting}
            sx={{
              height: 34,
              px: "13px",
              borderRadius: "9px",
              background: tAccent,
              color: "#fff",
              fontSize: "13px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: alpha(tAccent, 0.85), boxShadow: "none" },
              "&.Mui-disabled": { opacity: 0.5, color: "#fff", background: tAccent },
            }}
          >
            {reverting ? "Reverting…" : "Reopen for Editing"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
